import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import {
  MatDateFormats,
  MAT_DATE_FORMATS,
  MAT_NATIVE_DATE_FORMATS,
} from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { ngxCsv } from 'ngx-csv/ngx-csv';
import { DatePipe } from '@angular/common';
import { DashboardService } from '../../dashboard/dashboard.service';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import { PdfpreviewComponent } from './pdfpreview/pdfpreview.component';

export const GRI_DATE_FORMATS: MatDateFormats = {
  ...MAT_NATIVE_DATE_FORMATS,
  display: {
    ...MAT_NATIVE_DATE_FORMATS.display,
    dateInput: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    } as Intl.DateTimeFormatOptions,
  },
};

interface CsvReportInterface {
  disease: string;
  percentage_of_people_having_disease: number | string;
  total_registered_patients: number | string;
  total_patient_having_disease: number | string;
  males: number | string;
  females: number | string;
  total_new: number | string;
  new_males: number | string;
  new_females: number | string;
}

@Component({
  selector: 'app-tobacco-dashboard',
  templateUrl: './tobacco-dashboard.component.html',
  styleUrls: ['./tobacco-dashboard.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: GRI_DATE_FORMATS }],
})
export class TobaccoDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  // Pie chart
  @ViewChild('pieChartMain') chartMain!: ElementRef;
  myChart: any;
  public context!: CanvasRenderingContext2D;
  labels: string[] = [];
  selectedValue: any = 1;
  range = new FormGroup({
    start: new FormControl(),
    end: new FormControl(),
  });
  todayDate = new Date();
  todayDateString = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  filterOptions: any[] = [
    { value: 1, viewValue: 'All dates' },
    { value: 2, viewValue: 'Last 7 days' },
    { value: 3, viewValue: 'Last 15 days' },
    { value: 4, viewValue: 'Last 30 days' },
    { value: 5, viewValue: 'Last 45 days' },
    { value: 6, viewValue: 'Last 60 days' },
    { value: 7, viewValue: 'Custom date range' },
  ];
  healthFacilitySearchControl = new FormControl();
  filterHealthFacilitiesList: any[] = [];
  unSub!: Subscription;

  healthFacilitiesList!: any;
  selectedhealthFacilityId: any = -5; // set to default value less than 0, hence a negative integer -5
  customDate: any = {};
  tobaccoPatientsData: any = {};
  advisedToQuitData: any = {};
  readyToQuitData: any = {};
  hcpPatientsData: any = {};
  hcpToIntensiveData: any = {};
  prescribedNrtsData: any = {};
  tobaccoFreeData: any = {};
  showSplitByGender = false;
  duration: any = {};
  userTypeId = localStorage.getItem('userTypeId') || 0;
  defaultQueryString: any = {};
  startDateString : any;
  lastDateString : any;
  constructor(
    private dashboardService: DashboardService,
    private dialog: MatDialog,
    private datePipe: DatePipe
  ) {}
  ngOnInit(): void {
    this.unSub = this.healthFacilitySearchControl.valueChanges.subscribe(
      (res: string) => {
        this.filterHealthFacilitiesList = this.healthFacilitiesList.filter(
          (el: any) => {
            const searchStr = (
              el?.name +
              ', ' +
              el?.levelTwoName
            ).toLowerCase();
            return searchStr.indexOf(res?.toLowerCase()) !== -1;
          }
        );
      }
    );
    Chart.register(annotationPlugin);
    Chart.register(ChartDataLabels);
    Chart.register(zoomPlugin);
    Chart.defaults.color = '#ffffff'; // default text color for pie chart
    this.range.valueChanges.subscribe((res) => {
      if (res.start && res.end) {
        const startDateString = new Date(
          res.start.getTime() - res.start.getTimezoneOffset() * 60000
        )
          .toISOString()
          .split('T')[0];
        const lastDateString = new Date(
          res.end.getTime() - res.end.getTimezoneOffset() * 60000
        )
          .toISOString()
          .split('T')[0];
          this.startDateString = startDateString;
          this.lastDateString = lastDateString;
          this.updateDashboard();
        }
    });
    this.getAllHealthFacilities();

    if (+this.userTypeId > 2) {
      this.selectedhealthFacilityId = this.dashboardService.getLoggedInHospitalId();
      // Dashboard API with current(logged In) hostpitalID
      this.updateDashboard();
    } else {
      this.dashboardService.setQueryParams(this.defaultQueryString);
      this.fetchDashboardData();
      // this.openOutpatientVisits();
      // this.openDashboardPdfPreview();
    }
  }

  ngAfterViewInit(): void {
    // Get canvas which renders the Chart
    this.context = (
      this.chartMain.nativeElement as HTMLCanvasElement
    ).getContext('2d') as unknown as CanvasRenderingContext2D;
    this.loadPieChart();
  }

  ngOnDestroy(): void {
    if (this.unSub) this.unSub.unsubscribe();
  }

  legendConfig(): void {
    Chart.defaults.plugins.legend.position = 'bottom';
    Chart.defaults.plugins.legend.align = 'start';
    Chart.defaults.plugins.legend.labels.padding = 30;
    Chart.defaults.plugins.tooltip.enabled = true;
  }

  async loadPieChart(): Promise<any> {
    this.dashboardService
      .getTobaccoDashboardDataFor('tobacco-free-six-months')
      .subscribe((res) => {
        if (res !== null) {
          const piedata: number[] = [];
          const labelList: string[] = [];
          const bgColorList = [];

          if (res.noReferralToIntensive[0].totalNumber > 0) {
            labelList.push(' Without referral ');
            piedata.push(res.noReferralToIntensive[0].totalNumber);
            bgColorList.push('#5cbdfc');
          }
          if (res.referralToIntensive[0].totalNumber > 0) {
            labelList.push(' Referred, not offered NRT ');
            piedata.push(res.referralToIntensive[0].totalNumber);
            bgColorList.push('#006098');
          }
          if (res.referralWithNrt[0].totalNumber > 0) {
            labelList.push(' Referred & offered NRT ');
            piedata.push(res.referralWithNrt[0].totalNumber);
            bgColorList.push('#d86422');
          }

          this.myChart = new Chart(this.context, {
            type: 'pie',
            options: {
              responsive: false,
              plugins: {
                datalabels: {
                  formatter: (value, ctx) => {
                      let sum = 0;
                      let dataArr = ctx.chart.data.datasets[0].data;
                      dataArr.map((data:any) => {
                          sum += data;
                      });
                      let percentage = (value*100 / sum).toFixed(1)+"%";
                      return percentage;
                  },
                  color: '#fff',
              },
                legend: {
                  display: false,
                },
              },
            },
            data: {
              labels: labelList,
              datasets: [
                {
                  data: piedata,
                  backgroundColor: bgColorList,
                  hoverOffset: 0,
                  borderWidth: 0,
                },
              ],
            },
          });
          this.myChart.update();
        }
      });
  }

  updateChartData(){
    const res = this.tobaccoFreeData;
    console.log("update chart",res);
    
    if (res !== null) {
      
      const piedata: number[] = [];
      const labelList: string[] = [];
      const bgColorList = [];        
   
      if (res.noReferralToIntensive[0].totalNumber > 0) {
        labelList.push(' Without referral ');
        piedata.push(res.noReferralToIntensive[0].totalNumber);
        bgColorList.push('#5cbdfc');
      }
      if (res.referralToIntensive[0].totalNumber > 0) {
        labelList.push(' Referred, not offered NRT ');
        piedata.push(res.referralToIntensive[0].totalNumber);
        bgColorList.push('#006098');
      }
      if (res.referralWithNrt[0].totalNumber > 0) {
        labelList.push(' Referred & offered NRT ');
        piedata.push(res.referralWithNrt[0].totalNumber);
        bgColorList.push('#d86422');
      }
     const data = {
        labels: [...labelList],
        datasets: [
          {
            data: [...piedata],
            backgroundColor: bgColorList,
            hoverOffset: 0,
            borderWidth: 0,
          },
        ],
      }

 if(this.myChart)
 { 
  this.myChart.data = data
  this.myChart.update()
}
  }
}


  toggleDashboard() : void {

  }


  getAllHealthFacilities(): void {
    this.dashboardService.getAllHealthFacilities().subscribe((res: any) => {
      this.healthFacilitiesList = res.filter((el: any) => el.id !== -1);
      this.healthFacilitiesList.unshift({
        id: -5,
        name: 'All Health Facilities',
        code: '',
        levelThreeName: '',
        levelTwoName: '',
        levelOneName: '',
      });
      this.filterHealthFacilitiesList = this.healthFacilitiesList;

    });
    
  }

  getSelectedHospitalName(): string {
    return this.healthFacilitiesList.find((el: any) => {
      return el.id === this.selectedhealthFacilityId;
    })?.name;
  }
  getSelectedHospitalLevelTwoName(): string {
    return this.healthFacilitiesList.find((el: any) => {
      return el.id === this.selectedhealthFacilityId;
    })?.levelTwoName;
  }

  downloadCSV(): void {
    const csvArrObj: any = [];
    // Insert Tobacco Users row
    csvArrObj.push({
      Condition: 'Tobacco users',
      Total: this.tobaccoPatientsData.totalPatients,
      Percentage: this.tobaccoPatientsData.percentage,
      Daily_Total: this.tobaccoPatientsData.totalNumber[0],
      Daily_M: this.tobaccoPatientsData.maleNumber[0],
      Daily_F: this.tobaccoPatientsData.femaleNumber[0],
      Sometimes_Total: this.tobaccoPatientsData.totalNumber[1],
      Sometimes_M: this.tobaccoPatientsData.maleNumber[1],
      Sometimes_F: this.tobaccoPatientsData.femaleNumber[1],
      No_Total: this.tobaccoPatientsData.totalNumber[2],
      No_M: this.tobaccoPatientsData.maleNumber[2],
      No_F: this.tobaccoPatientsData.femaleNumber[2],
      Yes_Total: 'NA',
      Yes_M: 'NA',
      Yes_F: 'NA',
    });

    // Insert Tobacco users - advised to quit
    csvArrObj.push({
      Condition: 'Tobacco users - advised to quit',
      Total: this.advisedToQuitData.totalPatients,
      Percentage: this.advisedToQuitData.percentage,
      Daily_Total: 'NA',
      Daily_M: 'NA',
      Daily_F: 'NA',
      Sometimes_Total: 'NA',
      Sometimes_M: 'NA',
      Sometimes_F: 'NA',
      No_Total: this.advisedToQuitData.totalNumber[1],
      No_M: this.advisedToQuitData.maleNumber[1],
      No_F: this.advisedToQuitData.femaleNumber[1],
      Yes_Total: this.advisedToQuitData.totalNumber[0],
      Yes_M: this.advisedToQuitData.maleNumber[0],
      Yes_F: this.advisedToQuitData.femaleNumber[0],
    });

    // Insert Tobacco users - ready to quit
    csvArrObj.push({
      Condition: 'Tobacco users - ready to quit',
      Total: this.readyToQuitData.totalPatients,
      Percentage: this.readyToQuitData.percentage,
      Daily_Total: 'NA',
      Daily_M: 'NA',
      Daily_F: 'NA',
      Sometimes_Total: 'NA',
      Sometimes_M: 'NA',
      Sometimes_F: 'NA',
      No_Total: this.readyToQuitData.totalNumber[1],
      No_M: this.readyToQuitData.maleNumber[1],
      No_F: this.readyToQuitData.femaleNumber[1],
      Yes_Total: this.readyToQuitData.totalNumber[0],
      Yes_M: this.readyToQuitData.maleNumber[0],
      Yes_F: this.readyToQuitData.femaleNumber[0],
    });

    // Insert Tobacco users - developed quit plan with HCP
    csvArrObj.push({
      Condition: 'Tobacco users - developed quit plan with HCP',
      Total: this.hcpPatientsData.registeredPatients,
      Percentage: this.hcpPatientsData.percentage,
      Daily_Total: 'NA',
      Daily_M: 'NA',
      Daily_F: 'NA',
      Sometimes_Total: 'NA',
      Sometimes_M: 'NA',
      Sometimes_F: 'NA',
      No_Total: 'NA',
      No_M: 'NA',
      No_F: 'NA',
      Yes_Total: this.hcpPatientsData.totalNumber,
      Yes_M: this.hcpPatientsData.maleNumber,
      Yes_F: this.hcpPatientsData.femaleNumber,
    });

    // Insert Tobacco users - referred by HCP to intensive counselling
    csvArrObj.push({
      Condition: 'Tobacco users - referred by HCP to intensive counselling',
      Total: this.hcpToIntensiveData.registeredPatients,
      Percentage: this.hcpToIntensiveData.percentage,
      Daily_Total: 'NA',
      Daily_M: 'NA',
      Daily_F: 'NA',
      Sometimes_Total: 'NA',
      Sometimes_M: 'NA',
      Sometimes_F: 'NA',
      No_Total: 'NA',
      No_M: 'NA',
      No_F: 'NA',
      Yes_Total: this.hcpToIntensiveData.totalNumber,
      Yes_M: this.hcpToIntensiveData.maleNumber,
      Yes_F: this.hcpToIntensiveData.femaleNumber,
    });

    // Insert Tobacco users - developed quit plan and prescribed NRTs
    csvArrObj.push({
      Condition: 'Tobacco users - developed quit plan and prescribed NRTs',
      Total: this.prescribedNrtsData.registeredPatients,
      Percentage: this.prescribedNrtsData.percentage,
      Daily_Total: 'NA',
      Daily_M: 'NA',
      Daily_F: 'NA',
      Sometimes_Total: 'NA',
      Sometimes_M: 'NA',
      Sometimes_F: 'NA',
      No_Total: 'NA',
      No_M: 'NA',
      No_F: 'NA',
      Yes_Total: this.prescribedNrtsData.totalNumber,
      Yes_M: this.prescribedNrtsData.maleNumber,
      Yes_F: this.prescribedNrtsData.femaleNumber,
    });

    // Insert empty row
    csvArrObj.push({
      disease: '',
      percentage_of_people_having_disease: '',
      total_registered_patients: '',
      total_patient_having_disease: '',
      males: '',
      females: '',
      total_new: '',
      new_males: '',
      new_females: '',
    });

    // Headers for tobacco free 6 months
    csvArrObj.push({
      Condition: 'Tobacco free in 6 months',
      Count: 'Count',
      Without_referral_to_counselling_Total: 'No referral(Total)',
      Referred_no_NRT_Total: 'Referred (Total)',
      Referred_and_NRT_Total: 'Referred + NRT(Total)',
    });

    csvArrObj.push({
      Condition: '',
      Count: this.tobaccoFreeData.overallCount,
      Without_referral_to_counselling_Total:
        this.tobaccoFreeData.noReferralToIntensive[0].totalNumber,
      Referred_no_NRT_Total:
        this.tobaccoFreeData.referralToIntensive[0].totalNumber,
      Referred_and_NRT_Total:
        this.tobaccoFreeData.referralWithNrt[0].totalNumber,
    });

    // Insert empty row
    csvArrObj.push({
      disease: '',
      percentage_of_people_having_disease: '',
      total_registered_patients: '',
      total_patient_having_disease: '',
      males: '',
      females: '',
      total_new: '',
      new_males: '',
      new_females: '',
    });

    // Headers for split by gender
    csvArrObj.push({
      Condition: 'Split by gender',
      Column: '',
      Without_referral_to_counselling_Total: 'No referral',
      Referred_no_NRT_Total: 'Referred ',
      Referred_and_NRT_Total: 'Referred + NRT',
      Total: 'Total',
    });

    // Data for split by gender row Total
    csvArrObj.push({
      Condition: '',
      Column: 'Total',
      Without_referral_to_counselling_Total:
        this.tobaccoFreeData.noReferralToIntensive[0].totalNumber,
      Referred_no_NRT_Total:
        this.tobaccoFreeData.referralToIntensive[0].totalNumber,
      Referred_and_NRT_Total:
        this.tobaccoFreeData.referralWithNrt[0].totalNumber,
      Total:
        this.tobaccoFreeData.noReferralToIntensive[0].totalNumber +
        this.tobaccoFreeData.referralToIntensive[0].totalNumber +
        this.tobaccoFreeData.referralWithNrt[0].totalNumber,
    });

    // Data for split by gender row Male
    csvArrObj.push({
      Condition: '',
      Column: 'Male',
      Without_referral_to_counselling_Total:
        this.tobaccoFreeData.noReferralToIntensive[0].maleNumber,
      Referred_no_NRT_Total:
        this.tobaccoFreeData.referralToIntensive[0].maleNumber,
      Referred_and_NRT_Total:
        this.tobaccoFreeData.referralWithNrt[0].maleNumber,
      Total:
        this.tobaccoFreeData.noReferralToIntensive[0].maleNumber +
        this.tobaccoFreeData.referralToIntensive[0].maleNumber +
        this.tobaccoFreeData.referralWithNrt[0].maleNumber,
    });

    // Data for split by gender row Female
    csvArrObj.push({
      Condition: '',
      Column: 'Female',
      Without_referral_to_counselling_Total:
        this.tobaccoFreeData.noReferralToIntensive[0].femaleNumber,
      Referred_no_NRT_Total:
        this.tobaccoFreeData.referralToIntensive[0].femaleNumber,
      Referred_and_NRT_Total:
        this.tobaccoFreeData.referralWithNrt[0].femaleNumber,
      Total:
        this.tobaccoFreeData.noReferralToIntensive[0].femaleNumber +
        this.tobaccoFreeData.referralToIntensive[0].femaleNumber +
        this.tobaccoFreeData.referralWithNrt[0].femaleNumber,
    });

    // Insert empty row
    csvArrObj.push({
      disease: '',
      percentage_of_people_having_disease: '',
      total_registered_patients: '',
      total_patient_having_disease: '',
      males: '',
      females: '',
      total_new: '',
      new_males: '',
      new_females: '',
    });

    // Record date
    // Ignore the object keys, total_patient_having_disease key will show date of record generation
    let dateStr = '';
    if (this.selectedValue === 7) {
      dateStr = `${this.formatDate(
        this.customDate.startDate
      )} To ${this.formatDate(this.customDate.lastDate)}`;
    } else if (this.selectedValue === 1) {
      dateStr = 'All dates';
    } else {
      const days = this.getDaysFromId(this.selectedValue);
      const dateRange = this.getDateRange(days);
      dateStr = `${this.formatDate(dateRange.startDate)} To ${this.formatDate(
        dateRange.lastDate
      )}`;
    }
    csvArrObj.push({
      total_patient_having_disease: 'Record Generation from: ' + dateStr,
    });
    // object below is used to display Report generation Date
    // Ignore the object keys, total_patient_having_disease key will show date of report generation
    const currentDate = this.datePipe.transform(Date.now(), 'd MMM, y');
    csvArrObj.push({
      total_patient_having_disease: 'Generated on: ' + currentDate,
    });
    csvArrObj.push({
      total_patient_having_disease:
        'Health Facility: ' +
        this.getSelectedHospitalName() +
        ',' +
        this.getSelectedHospitalLevelTwoName(),
    });

    const options = {
      headers: [
        'Condition',
        'Total',
        'Percentage % or Count',
        'Daily(Total)',
        'Daily(M)',
        'Daily(F)',
        'Sometimes(Total)',
        'Sometimes(M)',
        'Sometimes(F)',
        'No(Total)',
        'No(M)',
        'No(F)',
        'Yes(Total)',
        'Yes(M)',
        'Yes(F)',
      ],
    };
    this.duration = this.filterOptions.find((el) => {
      return this.selectedValue === el.value;
    });
    let fileName = '';
    let duration = this.filterOptions.find((el) => {
      return this.selectedValue === el.value;
    });
    if (duration.value === 7) {
      duration = `${this.range.value.start.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })} - ${this.range.value.end.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })}`;
    } else duration = duration.viewValue;

    if (this.selectedValue === 7) {
      fileName = `Tobacco-cessation-dashboard-${duration}-${this.getSelectedHospitalName()}`;
    } else {
      fileName = `Tobacco-cessation-dashboard-${duration}-from-${
        this.todayDateString
      }-${this.getSelectedHospitalName()}`;
    }
    if (this.selectedValue === 1) {
      fileName = `Tobacco-cessation-dashboard-${duration}-${this.getSelectedHospitalName()}`;
    }
    fileName = fileName
      .replace(/\s/g, '-')
      .replace(/,/g, '')
      .replace(/---/g, '-');
    const csv = new ngxCsv(csvArrObj, fileName, options);
  }
  openDashboardPdfPreview(): void {
    let duration = this.filterOptions.find((el) => {
      return this.selectedValue === el.value;
    });
    if (duration.value === 7) {
      duration = `${this.range.value.start.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })} - ${this.range.value.end.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })}`;
    } else if (this.selectedValue === 1) duration = duration.viewValue;
    else {
      const days = this.getDaysFromId(this.selectedValue);
      const dateRange = this.getDateRange(days);
      duration = `${this.formatDate(dateRange.startDate)} To ${this.formatDate(
        dateRange.lastDate
      )}`;
    }

    const dialogRef = this.dialog.open(PdfpreviewComponent, {
      width: '21cm',
      maxHeight: '90vh',
      data: {
        tobaccoPatientsData: this.tobaccoPatientsData,
        advisedToQuitData: this.advisedToQuitData,
        readyToQuitData: this.readyToQuitData,
        hcpPatientsData: this.hcpPatientsData,
        hcpToIntensiveData: this.hcpToIntensiveData,
        prescribedNrtsData: this.prescribedNrtsData,
        tobaccoFreeData: this.tobaccoFreeData,
        hospitalName: this.getSelectedHospitalName(),
        levelTwoName: this.getSelectedHospitalLevelTwoName(),
        duration,
      },
    });
  }

  formatDate(date: any): string | null {
    return this.datePipe.transform(date, 'd MMM, y');
  }

  getDateRange(daysAgo: number): any {
    const startDate = new Date(
      new Date().setDate(new Date().getDate() - daysAgo)
    )
      .toISOString()
      .split('T')[0];
    const lastDate = new Date().toISOString().split('T')[0];

    return {
      startDate,
      lastDate,
      healthFacilityId: this.selectedhealthFacilityId,
    };
  }

  getDaysFromId(id: number): number {
    if (id === 2) return 7;
    else if (id === 3) return 15;
    else if (id === 4) return 30;
    else if (id === 5) return 45;
    else if (id === 6) return 60;

    return 0;
  }

  updateDashboard(): void {
    const queryString = {
      startDate: '1999-12-25',
      lastDate: '9999-12-25',
    };
    switch (this.selectedValue) {
      case 1:
        Object.assign(queryString, {
          startDate: '1999-12-25',
          lastDate: '9999-12-25',
          healthFacilityId: this.selectedhealthFacilityId,
        });
        this.dashboardService.setQueryParams(queryString);
        break;
      case 2:
        this.dashboardService.setQueryParams(this.getDateRange(7));
        Object.assign(queryString, this.getDateRange(7));
        break;
      case 3:
        this.dashboardService.setQueryParams(this.getDateRange(15));
        Object.assign(queryString, this.getDateRange(15));
        break;
      case 4:
        this.dashboardService.setQueryParams(this.getDateRange(30));
        Object.assign(queryString, this.getDateRange(30));
        break;
      case 5:
        this.dashboardService.setQueryParams(this.getDateRange(45));
        Object.assign(queryString, this.getDateRange(45));

        break;
      case 6:
        this.dashboardService.setQueryParams(this.getDateRange(60));
        Object.assign(queryString, this.getDateRange(60));

        break;
      case 7:
        Object.assign(queryString, {
          healthFacilityId: this.selectedhealthFacilityId,
        });
        this.dashboardService.setQueryParams(queryString);
        Object.assign(queryString, {
          startDate: this.startDateString,
          lastDate: this.lastDateString,
          healthFacilityId: this.selectedhealthFacilityId,
        });
        this.customDate = {
          startDate: this.startDateString,
          lastDate: this.lastDateString,
        };        
        this.dashboardService.setQueryParams(queryString);
        // Listen to date range event
        break;
      default:
        Object.assign(queryString, {
          startDate: '1999-12-25',
          lastDate: '9999-12-25',
        });
        break;
    }
    this.fetchDashboardData();
  }

  getApiTobaccoPatients(): void {
    this.dashboardService
      .getTobaccoDashboardDataFor('tobacco-patients')
      .subscribe((res: any) => {
        if (res !== null) {
          this.tobaccoPatientsData = res;
        } else {
          this.tobaccoPatientsData = {
            totalNumber: [],
            maleNumber: [],
            femaleNumber: [],
            totalPatients: null,
            tobaccoUsers: null,
            percentage: null,
          };
        }
      });
  }

  getApiAdvisedToQuit(): void {
    this.dashboardService
      .getTobaccoDashboardDataFor('advised-to-quit-patients')
      .subscribe((res: any) => {
        if (res !== null) {
          this.advisedToQuitData = res;
        } else {
          this.advisedToQuitData = {
            totalNumber: [],
            maleNumber: [],
            femaleNumber: [],
            totalPatients: null,
            percentage: null,
          };
        }
      });
  }

  getApiReadyToQuit(): void {
    this.dashboardService
      .getTobaccoDashboardDataFor('ready-to-quit-patients')
      .subscribe((res: any) => {
        if (res !== null) {
          this.readyToQuitData = res;
        } else {
          this.readyToQuitData = {
            totalNumber: [],
            maleNumber: [],
            femaleNumber: [],
            totalPatients: null,
            percentage: null,
          };
        }
      });
  }

  getApiHcpPatients(): void {
    this.dashboardService
      .getTobaccoDashboardDataFor('plan-with-hcp-patients')
      .subscribe((res: any) => {
        if (res !== null) {
          this.hcpPatientsData = res;
        } else {
          this.hcpPatientsData = {
            totalNumber: null,
            maleNumber: null,
            femaleNumber: null,
            totalPatients: null,
            registeredPatients: null,
            percentage: null,
          };
        }
      });
  }

  getApiHcpToIntensive(): void {
    this.dashboardService
      .getTobaccoDashboardDataFor('hcp-to-intensive-patients')
      .subscribe((res: any) => {
        if (res !== null) {
          this.hcpToIntensiveData = res;
        } else {
          this.hcpToIntensiveData = {
            totalNumber: null,
            maleNumber: null,
            femaleNumber: null,
            totalPatients: null,
            registeredPatients: null,
            percentage: null,
          };
        }
      });
  }

  getApiPrescribedNrts(): void {
    this.dashboardService
      .getTobaccoDashboardDataFor('prescribed-nrt-patients')
      .subscribe((res: any) => {
        if (res !== null) {
          this.prescribedNrtsData = res;
        } else {
          this.prescribedNrtsData = {
            totalNumber: null,
            maleNumber: null,
            femaleNumber: null,
            totalPatients: null,
            registeredPatients: null,
            percentage: null,
          };
        }
      });
  }

  getApiTobaccoFreeSixMonths(): void {
    // if(this.selectedhealthFacilityId!==-5){
    //   const params: any = {
    //     healthFacilityId: this.selectedhealthFacilityId,
    //   };
    //   this.dashboardService.setQueryParams(params);
    // }
    
    console.log(this.myChart,"My charttt");
    
    this.dashboardService
      .getTobaccoDashboardDataFor('tobacco-free-six-months')
      .subscribe((res: any) => {
        if (res !== null) {
          this.tobaccoFreeData = res;
          this.updateChartData();
        } else {
          this.tobaccoFreeData = {
            noReferralToIntensive: [],
            referralToIntensive: [],
            referralWithNrt: [],
            overallCount: null,
          };
          this.tobaccoFreeData.noReferralToIntensive[0] = [];
          this.tobaccoFreeData.referralToIntensive[0] = [];
          this.tobaccoFreeData.referralWithNrt[0] = [];
        }
      });
  }

  fetchDashboardData(): void {
    this.getApiTobaccoPatients();
    this.getApiAdvisedToQuit();
    this.getApiReadyToQuit();
    this.getApiHcpPatients();
    this.getApiHcpToIntensive();
    this.getApiPrescribedNrts();
    this.getApiTobaccoFreeSixMonths();
  }

  toggleSplit(): any {
    this.showSplitByGender = !this.showSplitByGender;
  }
}
